<script setup lang="ts">
  import { computed } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { Task } from '@stuut/api/models';
  import { humanize } from '@stuut/common/src/utils';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import TaskPriority from '@/components/common/TaskPriority.vue';
  import { PartnerQuickView } from '@/components/partners';
  import TasksAssigneeSelect from '@/components/tasks/TasksAssigneeSelect.vue';

  interface Props {
    showAssignee?: boolean;
    showClassifications?: boolean;
    showCustomer?: boolean;
    showPriority?: boolean;
    size?: '2xs' | 'xs' | 'sm';
    task: Task;
    textLabelClass?: HTMLAttributes['class'];
    truncate?: boolean;
  }

  const props = defineProps<Props>();

  const iconSizeClass = computed(() => {
    switch (props.size) {
      case '2xs':
      case 'xs':
        return 'text-xs!';
      case 'sm':
      default:
        return 'text-sm!';
    }
  });

  const customDescriptionIcon = computed(() => props.task?.customDescription?.icon);

  const customDescriptionLabel = computed(() => props.task?.customDescription?.label);
</script>

<template>
  <div
    class="flex items-center justify-between w-full gap-x-4"
    :class="cn(textLabelClass, { truncate })"
    data-component-name="TaskLabel"
  >
    <div class="flex flex-1 items-center justify-start gap-x-3">
      <div v-if="showPriority" class="flex w-14 shrink-0 justify-center">
        <TaskPriority :task="task" size="2xs" :urgent-always-badge="true" variant="indicator" />
      </div>

      <div class="grid grid-flow-col auto-cols-fr text-left w-full gap-x-5 truncate">
        <div v-if="showCustomer" class="flex items-center space-x-2 truncate">
          <span class="truncate font-semibold">{{ task.partner?.computedName }}</span>

          <PartnerQuickView :partner="task.partner ?? undefined">
            <RouterLink
              :to="{
                name: 'customers.customer',
                params: { id: task.partner?.id },
              }"
              @click.prevent
              @click.stop
            >
              <i
                class="fa-solid fa-up-right-from-square text-blue hover:text-blue-800 text-4xs!"
              ></i>
            </RouterLink>
          </PartnerQuickView>
        </div>

        <div class="flex items-center gap-x-1.5 truncate">
          <i
            v-if="task.taskTypeIcon"
            class="text-center w-6"
            :class="cn(task.taskTypeIcon, task.taskTypeIconClass, iconSizeClass)"
          />

          <span class="truncate">{{ task.taskType }}</span>
        </div>

        <div v-if="showClassifications" class="truncate">
          <div v-if="customDescriptionLabel" class="flex items-center gap-x-1.5 truncate">
            <Tooltip :delay-duration="0">
              <TooltipTrigger as-child>
                <i v-if="customDescriptionIcon" :class="customDescriptionIcon" />

                <span class="truncate">{{ customDescriptionLabel }}</span>
              </TooltipTrigger>

              <TooltipContent side="top">
                <i v-if="customDescriptionIcon" :class="customDescriptionIcon" />

                <span class="truncate">{{ customDescriptionLabel }}</span>
              </TooltipContent>
            </Tooltip>
          </div>

          <span v-else class="truncate">
            {{ task.classifications?.map((c) => humanize(c)).join(', ') }}
          </span>
        </div>
      </div>

      <slot name="task-label-right" />
    </div>

    <div v-if="showAssignee" class="w-6" @click.stop>
      <TasksAssigneeSelect :task="task" :show-avatar-only="true" />
    </div>
  </div>
</template>

<style scoped></style>
